import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChatPanelMaster } from './ChatPage/ChatPanelMaster'
import { TopNavigation } from '../components/TopNavigation'
import { AgentProfileHeader } from '../components/AgentProfile/AgentProfileHeader'
import { AgentProfileInfo } from '../components/AgentProfile/AgentProfileInfo'
import { AgentProfileSections } from '../components/AgentProfile/AgentProfileSections'
import { EditAgentProfileModal } from '../components/AgentProfile/EditAgentProfileModal'
import { agentService, type Agent } from '../services/agentService'
import { useChatLayout } from '../hooks/useChatLayout'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { AgentSidebar } from '../components/AgentProfile/AgentSidebar'



export function AgentProfilePage() {
    const { handle } = useParams<{ handle: string }>()
    const navigate = useNavigate()

    const [agent, setAgent] = useState<Agent | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isChatOpen, setIsChatOpen] = useState(false)
    const [isResizing, setIsResizing] = useState(false)
    const [isChatFullscreen, setIsChatFullscreen] = useState(false)


    // Use chat layout hook for persistent width
    const { chatWidth, setChatWidth } = useChatLayout('agent_profile', 'right', 600)

    useEffect(() => {
        const fetchAgent = async () => {
            if (!handle) {
                setIsLoading(false)
                return
            }

            setIsLoading(true)
            const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle
            const agentData = await agentService.getAgentByHandle(cleanHandle)
            setAgent(agentData)
            setIsLoading(false)
        }

        fetchAgent()
    }, [handle])



    const handleConfigure = () => {
        if (agent) {
            navigate(`/agentes/${agent.handle}/configuracao`)
        }
    }

    const handleEditProfile = () => {
        setIsEditModalOpen(true)
    }

    const handleSaveProfile = async (updates: Partial<Agent>) => {
        if (!agent) return

        try {
            console.log('handleSaveProfile called with updates:', updates)
            const updatedAgent = await agentService.updateAgentProfile(agent.id, updates)
            console.log('Updated agent received:', updatedAgent)
            setAgent(updatedAgent)
            console.log('Agent state updated')
        } catch (error) {
            console.error('Error updating agent profile:', error)
            throw error
        }
    }

    // Handle chat resize
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        setIsResizing(true)
    }

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return

            const newWidth = window.innerWidth - e.clientX
            // Limit width between 300px and 800px
            if (newWidth >= 300 && newWidth <= 800) {
                setChatWidth(newWidth)
            }
        }

        const handleMouseUp = () => {
            setIsResizing(false)
        }

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing, setChatWidth])

    // Show TopNavigation while loading
    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-black">
                <TopNavigation />
            </div>
        )
    }

    // Show error only after loading completes
    if (!agent) {
        return (
            <div className="min-h-screen bg-white dark:bg-black">
                <TopNavigation />
                <div className="pt-20 px-6 max-w-4xl mx-auto text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Agente não encontrado
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        O agente que você está procurando não existe ou foi removido.
                    </p>
                    <button
                        onClick={() => navigate('/agentes')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                    >
                        Voltar para Agentes
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="relative min-h-screen bg-white dark:bg-black">
            {/* Top Navigation */}
            <div className="fixed top-0 left-0 right-0 z-[100]">
                <TopNavigation />
            </div>



            {/* Main Content - Profile Layout */}
            <div className="pt-12 flex h-screen relative">
                {/* Fixed Agent Sidebar */}
                {agent && (
                    <AgentSidebar
                        agent={agent}
                        onChatClick={() => setIsChatOpen(!isChatOpen)}
                    />
                )}

                {/* Center Column - Agent Profile */}
                <div className="flex-1 h-full overflow-y-auto">
                    <div className="max-w-[600px] mx-auto bg-white dark:bg-black min-h-screen px-4">
                        {/* Profile Header (Banner + Avatar + Buttons) */}
                        <AgentProfileHeader
                            agent={agent}
                            onBack={() => navigate('/agentes')}
                            onChat={() => setIsChatOpen(!isChatOpen)}
                            onConfigure={handleConfigure}
                            onEditProfile={handleEditProfile}
                        />

                        {/* Profile Info (Name, Badge, Handle, Description, Date) */}
                        <AgentProfileInfo agent={agent} />

                        {/* Divider */}
                        <div className="border-t border-gray-200 dark:border-gray-800 my-6" />

                        {/* Profile Sections (About, Function) */}
                        <AgentProfileSections agent={agent} />
                    </div>
                </div>

                {/* Right Column - ChatPanelMaster (Absolute Positioned Overlay) */}
                <AnimatePresence>
                    {isChatOpen && (
                        <motion.div
                            key="chat-panel"
                            {...({
                                initial: { x: '100%' },
                                animate: { x: 0 },
                                exit: { x: '100%' }
                            } as any)}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{ width: isChatFullscreen ? '100%' : `${chatWidth}px` }}
                            className={`absolute top-12 h-[calc(100%-3rem)] ${!isChatFullscreen ? 'border-l border-gray-200 dark:border-gray-800' : ''} bg-white dark:bg-black hidden lg:block ${isChatFullscreen ? 'left-0 z-[90]' : 'right-0 z-50'}`}
                        >
                            {/* Resize Handle - hide in fullscreen */}
                            {!isChatFullscreen && (
                                <div
                                    onMouseDown={handleMouseDown}
                                    className="absolute left-0 top-0 w-4 h-full cursor-ew-resize -translate-x-1.5"
                                />
                            )}

                            <ErrorBoundary>
                                <ChatPanelMaster
                                    isOpen={true}
                                    onClose={() => setIsChatOpen(false)}
                                    onFullscreenChange={setIsChatFullscreen}
                                    isFullscreen={isChatFullscreen}
                                    agentId={agent.id}
                                    agentName={agent.name}
                                    agent={agent} // Pass full agent object
                                    isEmbedded={true}
                                    position="right"
                                />
                            </ErrorBoundary>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Edit Profile Modal */}
            {agent && (
                <EditAgentProfileModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    agent={agent}
                    onSave={handleSaveProfile}
                />
            )}
        </div>
    )
}
